import { Controller } from "@hotwired/stimulus";
import { Turbo } from "@hotwired/turbo-rails";

// Drives a list of turbo-frame rows that can each be edited in place, with a
// "Save all" button that commits every open row in one request. Shared by the
// processing-services list and the candidate-steps checklist — framePrefix and
// paramKey are what differ between them.
export default class extends Controller {
  static targets = ["frame", "saveAllButton", "editingForm"];
  static values = {
    bulkUpdateUrl: String,
    framePrefix: String,
    paramKey: String,
  };

  connect() {
    this.observer = new MutationObserver(() => this.updateState());
    this.observer.observe(this.frameTarget, { childList: true, subtree: true });
    this.updateState();
  }

  disconnect() {
    this.observer?.disconnect();
  }

  async saveAll() {
    const forms = this.editingForms();
    if (forms.length === 0) return;

    // Submitting each row's own form independently (form.requestSubmit()) raced against
    // Turbo's handling of same-named nested frames and could wipe out unrelated rows, so
    // instead combine every editing row's fields into one request against a bulk-update
    // endpoint that only ever touches the rows it was given.
    const prefix = this.framePrefixValue;
    const singular = prefix.replace(/_$/, "");
    const fieldPattern = new RegExp(`^${singular}\\[(.+)\\]$`);

    const body = new FormData();
    forms.forEach((form) => {
      const id = form.closest(`turbo-frame[id^="${prefix}"]`).id.replace(prefix, "");
      new FormData(form).forEach((value, key) => {
        const match = key.match(fieldPattern);
        if (match) body.append(`${this.paramKeyValue}[${id}][${match[1]}]`, value);
      });
    });

    // Guards against a stale click: the button's visibility lags DOM changes by a
    // MutationObserver tick, so by the time this runs the rows it saw may already be
    // gone/changed and produced no fields at all — sending that empties out the required
    // collection param server-side and 400s.
    if (Array.from(body.keys()).length === 0) return;

    this.saveAllButtonTarget.disabled = true;
    try {
      const response = await fetch(this.bulkUpdateUrlValue, {
        method: "PATCH",
        body,
        headers: {
          Accept: "text/vnd.turbo-stream.html",
          "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.content,
        },
      });
      // A non-2xx response body is an HTML error page, not a turbo-stream fragment —
      // feeding that to Turbo does nothing useful and leaves the rows looking stuck.
      if (!response.ok) return;
      Turbo.renderStreamMessage(await response.text());
    } finally {
      this.saveAllButtonTarget.disabled = false;
    }
  }

  updateState() {
    const editing = this.editingForms().length >= 2;
    this.saveAllButtonTarget.classList.toggle("hidden", !editing);
    this.saveAllButtonTarget.disabled = !editing;
  }

  editingForms() {
    // Scoped to the editingForm target (not just any `form` under the frame) because a row
    // may also carry button_to forms (destroy, file purge, payment removal) which would
    // otherwise be miscounted as "being edited".
    return this.editingFormTargets;
  }
}
